import React from 'react';
import {InlineMath} from 'react-katex';
import 'katex/dist/katex.min.css';

export const LaTeXPreviewContent = ({content}) => {
    if (!content) {
        return null;
    }

    const renderContent = (text) => {
        // Process <VISUAL> tags first
        const visualComponents = [];
        let processedText = text;

        // Extract <VISUAL> tags
        const visualRegex = /<VISUAL>([\s\S]*?)<\/VISUAL>/g;
        let visualMatch;
        let visualIndex = 0;
        let offset = 0;

        while ((visualMatch = visualRegex.exec(text)) !== null) {
            const placeholder = `__VISUAL_${visualIndex}__`;
            const beforeVisual = processedText.substring(0, visualMatch.index - offset);
            const afterVisual = processedText.substring(
                visualMatch.index + visualMatch[0].length - offset
            );

            // Store visual component
            visualComponents.push({
                index: visualIndex,
                content: visualMatch[1]
            });

            // Replace visual tag with placeholder
            processedText = beforeVisual + placeholder + afterVisual;

            // Update offset for next replacement
            offset += visualMatch[0].length - placeholder.length;
            visualIndex++;
        }

        // Split content by code blocks first
        const segments = [];
        let currentIndex = 0;
        const codeBlockRegex = /```[\s\S]*?```/g;
        let match;

        while ((match = codeBlockRegex.exec(processedText)) !== null) {
            // Add text before code block
            if (match.index > currentIndex) {
                segments.push({
                    type: 'text',
                    content: processedText.slice(currentIndex, match.index)
                });
            }

            // Add code block
            segments.push({
                type: 'code',
                content: match[0].slice(3, -3)  // Remove ``` from start and end
            });

            currentIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (currentIndex < processedText.length) {
            segments.push({
                type: 'text',
                content: processedText.slice(currentIndex)
            });
        }

        // Process each segment
        return segments.map((segment, index) => {
            if (segment.type === 'code') {
                return (
                    <pre key={index}
                         className="bg-gray-100 rounded-md p-2 my-2 font-mono text-sm whitespace-pre-wrap break-all"
                         style={{wordBreak: 'break-all', overflowWrap: 'break-word'}}>
                        {segment.content}
                    </pre>
                );
            } else {
                // Process LaTeX in text segments
                const lines = segment.content.split('\n');
                return lines.map((line, lineIndex) => {
                    // Process visual placeholders first
                    const parts = [];
                    let currentLine = line;

                    // Check for visual placeholders
                    for (const visual of visualComponents) {
                        const placeholder = `__VISUAL_${visual.index}__`;
                        if (currentLine.includes(placeholder)) {
                            const splitByVisual = currentLine.split(placeholder);

                            // Process the parts before the visual
                            if (splitByVisual[0]) {
                                parts.push({
                                    type: 'text',
                                    content: splitByVisual[0]
                                });
                            }

                            // Add visual
                            parts.push({
                                type: 'visual',
                                content: visual.content
                            });

                            // Update current line to be the remainder
                            currentLine = splitByVisual.slice(1).join('');
                        }
                    }

                    // Add any remaining text
                    if (currentLine) {
                        parts.push({
                            type: 'text',
                            content: currentLine
                        });
                    }

                    // If we found visual parts, render them specially
                    if (parts.length > 0) {
                        return (
                            <React.Fragment key={`${index}-${lineIndex}`}>
                                {parts.map((part, partIndex) => {
                                    if (part.type === 'visual') {
                                        return (
                                            <div
                                                key={`visual-${index}-${lineIndex}-${partIndex}`}
                                                className="my-2 p-3 border border-gray-300 rounded-md bg-gray-50"
                                            >
                                                <div className="flex items-center mb-1">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        className="w-4 h-4 text-gray-500 mr-1"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                    <span className="text-xs font-medium text-gray-500">Visual Description</span>
                                                </div>
                                                <div className="text-gray-700 text-sm">{part.content.trim()}</div>
                                            </div>
                                        );
                                    } else {
                                        // Process LaTeX in the text part
                                        const latexParts = part.content.split(/(?<!\\)(\$.*?(?<!\\)\$)/g);
                                        return latexParts.map((latexPart, latexIndex) => {
                                            if (latexPart.startsWith('$') && latexPart.endsWith('$') && !latexPart.startsWith('\\$')) {
                                                const latex = latexPart.slice(1, -1)
                                                    .replace(/\\\$/g, '$');
                                                try {
                                                    return <InlineMath key={`latex-${partIndex}-${latexIndex}`} math={latex}/>;
                                                } catch (error) {
                                                    return <span key={`latex-error-${partIndex}-${latexIndex}`} className="text-red-500">{latexPart}</span>;
                                                }
                                            }
                                            return <span key={`text-${partIndex}-${latexIndex}`}>{latexPart.replace(/\\\$/g, '$')}</span>;
                                        });
                                    }
                                })}
                                {lineIndex < lines.length - 1 && <br/>}
                            </React.Fragment>
                        );
                    } else {
                        // Original LaTeX processing if no visuals
                        const parts = line.split(/(?<!\\)(\$.*?(?<!\\)\$)/g);
                        const processedLine = parts.map((part, partIndex) => {
                            if (part.startsWith('$') && part.endsWith('$') && !part.startsWith('\\$')) {
                                const latex = part.slice(1, -1)
                                    .replace(/\\\$/g, '$'); // Replace escaped dollar signs with actual dollar signs
                                try {
                                    return <InlineMath key={`${index}-${lineIndex}-${partIndex}`} math={latex}/>;
                                } catch (error) {
                                    return <span key={`${index}-${lineIndex}-${partIndex}`}
                                                 className="text-red-500">{part}</span>;
                                }
                            }
                            // Replace escaped dollar signs with actual dollar signs in regular text
                            return <span key={`${index}-${lineIndex}-${partIndex}`}>{part.replace(/\\\$/g, '$')}</span>;
                        });

                        return (
                            <React.Fragment key={`${index}-${lineIndex}`}>
                                {processedLine}
                                {lineIndex < lines.length - 1 && <br/>}
                            </React.Fragment>
                        );
                    }
                });
            }
        });
    };

    return <div className="latex-content">{renderContent(content)}</div>;
};